import { RWSService } from "@rws-framework/client";
import { hasCustomQuestions, getIPACustomQuestions, getSyntaxTreeCustomQuestions, getIpaCqtBaseUrl, getSyntaxTreeCqtBaseUrl } from "./cqtRegistry";

// Surface a failed authoring-script load. Without this the Question Editor just
// renders without its custom tooling and gives the author no clue why, so a bad
// deploy or an unreachable bucket looks like the feature silently vanished.
// Raised on the host document so it goes through the app's own notification
// system (jnct:notifications:error, see eheader.js) rather than any bespoke UI.
// Only authors are ever in a position to see it — these scripts are authoring-side.
//
// Deliberately a module function rather than a method — the injectors below are
// invoked as `this._registry[q][1](doc)`, so their `this` is the registry array,
// not the service, and a method call here would throw inside an async onerror.
function notifyCqtLoadFailed(failedUrl: string) {
  try {
    console.error(`[CQT] Failed to load custom question authoring script: ${failedUrl}`);
    document.documentElement.dispatchEvent(
      new CustomEvent("jnct:notifications:error", {
        detail: "Some question editing tools failed to load. Reload the page before editing this question.",
      })
    );
  } catch (e) {
    // reporting must never break the editor
  }
}

class LearnosityCustomQuestionsService extends RWSService {
  static _IN_CLIENT: boolean = true;

  // TODO(reseller) bit odd that this resolution is so similar to the one in hasCustomQuestions
  _registry = {
    'ipa': [getIPACustomQuestions, this.injectIpaEditorKeyboard],
    'syntaxtree': [getSyntaxTreeCustomQuestions, this.injectSyntaxTreeEditorTree]
  }

  getCustomQuestions() {
    return hasCustomQuestions("").map(q => [...(this._registry[q][0])()][0])
  }

  injectCustomQuestions(iframeDoc: Document) {
    if (!window.edrnaConfig?.cqt_base_url) return;

    hasCustomQuestions("").forEach(q => {
      this._registry[q][1](iframeDoc)
    })
  }

  // Inject the IPA Transcription CQT's authoring keyboard into the editor iframe.
  // The script self-gates on a marker element (.ipa-cqt-keyboard-mount) that only
  // exists for that question type, so this is a harmless no-op for every other
  // question. (Learnosity's editor_layout is HTML-only and can't run JS itself.)
  private injectIpaEditorKeyboard(doc: Document) {
    try {
      const ipaBase = getIpaCqtBaseUrl();
      if (!ipaBase) return;
      if (doc.getElementById("ipa-editor-keyboard-script")) return;
      const script = doc.createElement("script");
      script.id = "ipa-editor-keyboard-script";
      script.src = ipaBase + "editor-keyboard.js";
      script.onerror = () => notifyCqtLoadFailed(script.src);
      doc.body.appendChild(script);
    } catch (e) {
      console.error("Failed to inject IPA editor keyboard", e);
    }
  }

  // Inject the Syntax Tree CQT's authoring tree builder into the editor iframe.
  // The script self-gates on .st-cqt-tree-builder-mount (only present for this
  // question type), so it is a harmless no-op for every other question type.
  private injectSyntaxTreeEditorTree(doc: Document) {
    try {
      const stBase = getSyntaxTreeCqtBaseUrl();
      if (!stBase) return;
      if (doc.getElementById("st-editor-tree-script")) return;
      const script = doc.createElement("script");
      script.id  = "st-editor-tree-script";
      script.src = stBase + "editor-tree.js?v=25";
      script.onerror = () => notifyCqtLoadFailed(script.src);
      doc.body.appendChild(script);
    } catch (e) {
      console.error("Failed to inject syntax-tree editor-tree script", e);
    }
  }
}

export default LearnosityCustomQuestionsService.getSingleton();

export { LearnosityCustomQuestionsService as LearnosityCustomQuestionsServiceInstance };
